import { Box, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { HeatMapCountryMetaData } from '../../../../lcpn-uploader/server/services/parse-map-records/heat-map/heat-map-types';
import { Icon } from '../../Icon';
import { theme } from '../../../styles/theme';
import { hexToRgba } from '../../../utils/colour-utils';
import { getStatusConfigByDataValue } from '../../../map-data/heatmap-legend-config';

import type { Heading, Section } from './types';

const getStageProperties = (countryStatus?: string) => {
  if (!countryStatus) return;
  const config = getStatusConfigByDataValue(countryStatus);
  if (!config || !config.active) return;
  return {
    color: config.textColor,
    backgroundColor: config.color,
    stage: config.stage,
    translationString: `countryStatus.${config.translationKey}`,
  };
};

export function useHeatMapDataTableDefinitions() {
  const { t } = useTranslation();
  const sections: Section[] = [
    {
      title: t('countryRow.definitionTables.participantOutcomesLDCT.title'),
      backgroundColor: theme.colors['green-100'],
      rows: [
        {
          title: t(
            'countryRow.definitionTables.participantOutcomesLDCT.previousTrials',
          ),
          key: 'previousLcdtScreening',
        },
        {
          title: t(
            'countryRow.definitionTables.participantOutcomesLDCT.pilots',
          ),
          key: 'pilotsLcdtScreening',
        },
        {
          title: t(
            'countryRow.definitionTables.participantOutcomesLDCT.organisedProgrammes',
          ),
          key: 'hasOrganisedLdctProgramme',
        },
      ],
    },
    {
      title: t('countryRow.definitionTables.participantOutcomesNCCP.title'),
      backgroundColor: theme.colors['green-100'],
      rows: [
        {
          title: t(
            'countryRow.definitionTables.participantOutcomesNCCP.availability',
          ),
          key: 'nccpAvailable',
          fallback: '—',
        },
        {
          title: t(
            'countryRow.definitionTables.participantOutcomesNCCP.previousNccpPublished',
          ),
          key: 'previousNccpPublished',
          fallback: '—',
        },
        {
          title: t(
            'countryRow.definitionTables.participantOutcomesNCCP.lastPublished',
          ),
          key: 'nccpLastUpdated',
        },
        {
          title: t(
            'countryRow.definitionTables.participantOutcomesNCCP.lungCancerInNccp',
          ),
          key: 'lungCancerInNccp',
          fallback: '—',
        },
        {
          title: t(
            'countryRow.definitionTables.participantOutcomesNCCP.earlyDetection',
          ),
          key: 'earlyDetectionInNccp',
          fallback: '—',
        },
        {
          title: t(
            'countryRow.definitionTables.participantOutcomesNCCP.fundingForEarlyDetection',
          ),
          key: 'fundingForEarlyDetection',
          fallback: '—',
        },
        {
          title: t('countryRow.definitionTables.participantOutcomesNCCP.PBCR'),
          key: 'pbcr',
          fallback: '—',
        },
      ],
    },
    {
      title: t(
        'countryRow.definitionTables.participantOutcomesIncidence.title',
      ),
      backgroundColor: theme.colors['blue-100'],
      rows: [
        {
          title: t(
            'countryRow.definitionTables.participantOutcomesIncidence.bothSexes',
          ),
          key: 'incidenceCombined',
          format: v => v ?? '—',
        },
        {
          title: t(
            'countryRow.definitionTables.participantOutcomesIncidence.male',
          ),
          key: 'incidenceMale',
          format: v => v ?? '—',
        },
        {
          title: t(
            'countryRow.definitionTables.participantOutcomesIncidence.female',
          ),
          key: 'incidenceFemale',
          format: v => v ?? '—',
        },
        {
          title: t(
            'countryRow.definitionTables.participantOutcomesIncidence.globocanYear',
          ),
          key: 'globocanYear',
          format: v => v ?? '2020',
        },
      ],

      notes: [
        {
          title: t(
            'countryRow.definitionTables.participantOutcomesIncidence.footnotesTitle',
          ),
          body: (item: HeatMapCountryMetaData) =>
            t(
              'countryRow.definitionTables.participantOutcomesIncidence.footnotesBody',
              {
                replace: { year: item.globocanYear ?? '2020' },
              },
            ),
        },
      ],
    },
    {
      title: t(
        'countryRow.definitionTables.participantOutcomesMortality.title',
      ),
      backgroundColor: theme.colors['blue-100'],
      rows: [
        {
          title: t(
            'countryRow.definitionTables.participantOutcomesMortality.bothSexes',
          ),
          key: 'mortalityCombined',
          format: v => v ?? '—',
        },
        {
          title: t(
            'countryRow.definitionTables.participantOutcomesMortality.male',
          ),
          key: 'mortalityMale',
          format: v => v ?? '—',
        },
        {
          title: t(
            'countryRow.definitionTables.participantOutcomesMortality.female',
          ),
          key: 'mortalityFemale',
          format: v => v ?? '—',
        },
        {
          title: t(
            'countryRow.definitionTables.participantOutcomesMortality.globocanYear',
          ),
          key: 'globocanYear',
          format: v => v ?? '2020',
        },
      ],

      notes: [
        {
          title: t(
            'countryRow.definitionTables.participantOutcomesMortality.footnotesTitle',
          ),
          body: (item: HeatMapCountryMetaData) =>
            t(
              'countryRow.definitionTables.participantOutcomesMortality.footnotesBody',
              {
                replace: { year: item.globocanYear ?? '2020' },
              },
            ),
        },
      ],
    },
    {
      title: t(
        'countryRow.definitionTables.participantOutcomesAdditional.title',
      ),
      backgroundColor: '#333740',
      rows: [
        {
          title: t(
            'countryRow.definitionTables.participantOutcomesAdditional.lastUpdated',
          ),
          key: 'updated',
          format: v => (v ?? '').toString(),
        },
        {
          title: t(
            'countryRow.definitionTables.participantOutcomesAdditional.footnotesTitle',
          ),
          key: 'references',
          typographyVariant: 'p3',
        },
      ],
    },
  ];

  const headings: Heading<HeatMapCountryMetaData>[] = [
    {
      backgroundColor: theme.colors['green-100'],
      color: 'white',
      typographyVariant: 'h4',
      key: item => (
        <>
          <Stack direction={'row'} alignItems={'flex-start'} spacing={1}>
            <Box>
              <Icon width={20} height={20} variant={'MapInverted'} />
            </Box>
            <div>{item.countryName}</div>
          </Stack>
        </>
      ),
    },
    {
      backgroundColor: hexToRgba(theme.colors['green-100'], 0.6),
      color: 'white',
      mergeCells: true,
      typographyVariant: 'p1',
      key: () => <>{t('comparisonTable.heatMap.stageText')}</>,
    },
    {
      backgroundColor: item =>
        getStageProperties(item.countryStatus)!.backgroundColor,
      color: item => getStageProperties(item.countryStatus)!.color,
      typographyVariant: 'p1',
      key: item => {
        const stageProperties = getStageProperties(item.countryStatus);
        if (!stageProperties) return <></>;
        return (
          <>
            <div>
              {t('comparisonTable.heatMap.stageNumber', {
                replace: { stage: stageProperties.stage },
              })}
              <br />
              {t(stageProperties.translationString)}
            </div>
          </>
        );
      },
    },
    {
      typographyVariant: 'p3',
      key: 'countryStatusFootnote',
    },
  ];

  return { headings, sections };
}
